import { API_ENDPOINTS } from '@/config/api';
import { apiClient } from '@/services/api/client';
import type { BackendApiResponse } from '@/types/general-enquiry';
import type { Suburb, SuburbListResponse, SuburbWrite } from '@/types/suburb';

export interface SuburbListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'id' | 'name' | 'displayOrder' | 'isActive' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  isActive?: boolean;
}

export interface SuburbListResult {
  items: Suburb[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const suburbsApi = {
  async list(params: SuburbListParams = {}): Promise<SuburbListResult> {
    const response = await apiClient.get<BackendApiResponse<SuburbListResponse>>(
      API_ENDPOINTS.suburbs.list,
      {
        params,
        skipSuccessToast: true,
        skipErrorToast: true,
      },
    );

    const data = response?.data?.data;
    return {
      items: data?.suburbs ?? [],
      page: data?.meta?.page ?? 1,
      limit: data?.meta?.limit ?? 10,
      total: data?.meta?.total ?? 0,
      totalPages: data?.meta?.totalPages ?? 0,
    };
  },

  async getBySlug(slug: string): Promise<Suburb> {
    const response = await apiClient.get<BackendApiResponse<Suburb>>(
      API_ENDPOINTS.suburbs.getBySlug(slug),
      {
        skipSuccessToast: true,
        skipErrorToast: true,
      },
    );
    return response.data.data;
  },

  async create(data: SuburbWrite): Promise<Suburb> {
    const response = await apiClient.post<BackendApiResponse<Suburb>>(
      API_ENDPOINTS.suburbs.create,
      data,
      { skipErrorToast: true },
    );
    return response.data.data;
  },

  async update(id: number | string, data: SuburbWrite): Promise<Suburb> {
    const response = await apiClient.patch<BackendApiResponse<Suburb>>(
      API_ENDPOINTS.suburbs.update(id),
      data,
      { skipErrorToast: true },
    );
    return response.data.data;
  },

  async updateStatus(
    id: number | string,
    data: { isActive: boolean },
  ): Promise<{ id: number; isActive: boolean }> {
    const response = await apiClient.patch<BackendApiResponse<{ id: number; isActive: boolean }>>(
      API_ENDPOINTS.suburbs.updateStatus(id),
      data,
    );
    return response.data.data;
  },

  async delete(id: number | string): Promise<{ id: number; deleted: boolean }> {
    const response = await apiClient.delete<BackendApiResponse<{ id: number; deleted: boolean }>>(
      API_ENDPOINTS.suburbs.delete(id),
    );
    return response.data.data;
  },

  async reorder(data: { sourceId: number; targetId: number }): Promise<void> {
    await apiClient.patch<BackendApiResponse<void>>(API_ENDPOINTS.suburbs.reorder, data);
  },
};
